import { useCallback, useMemo, useState } from 'react'
import {
  type RiskReductionMeasure,
  type SafetyFunction,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { RiskReductionMeasureForm } from './RiskReductionMeasureForm'
import {
  defaultRiskReductionMeasureFormData,
  type RiskReductionMeasureFormData,
  validateRiskReductionMeasureForm,
} from './RiskReductionMeasureForm.schema'

type RiskReductionMeasureDialogProps = {
  isOpen: boolean
  onClose: () => void
  onCreate: (data: RiskReductionMeasureFormData) => void
  onUpdate?: (
    payload: { riskReductionMeasureId: string } & RiskReductionMeasureFormData
  ) => void
  riskReductionMeasure?: RiskReductionMeasure | null
  safetyFunctions?: SafetyFunction[]
  isCreating?: boolean
  isUpdating?: boolean
}

function toFormData(
  riskReductionMeasure: RiskReductionMeasure | null | undefined
): RiskReductionMeasureFormData {
  if (riskReductionMeasure == null) {
    return defaultRiskReductionMeasureFormData
  }

  return {
    name: riskReductionMeasure.name,
    type: riskReductionMeasure.type,
    description: riskReductionMeasure.description ?? '',
    dependsOnControlSystem:
      riskReductionMeasure.dependsOnControlSystem ?? false,
    safetyFunctionId: riskReductionMeasure.safetyFunctionId ?? null,
    ehsrClauseIds: riskReductionMeasure.ehsrClauseIds ?? [],
  }
}

export function RiskReductionMeasureDialog({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  riskReductionMeasure,
  safetyFunctions = [],
  isCreating = false,
  isUpdating = false,
}: RiskReductionMeasureDialogProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const isEdit = riskReductionMeasure != null
  const [formData, setFormData] = useState<RiskReductionMeasureFormData>(() =>
    toFormData(riskReductionMeasure)
  )

  const isValid = useMemo(() => {
    return validateRiskReductionMeasureForm(formData).success
  }, [formData])

  const isPending = isCreating || isUpdating
  const canSubmit = isValid && !isPending

  const handleClose = useCallback(() => {
    setFormData(toFormData(riskReductionMeasure))
    onClose()
  }, [onClose, riskReductionMeasure])

  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      return
    }

    if (isEdit && riskReductionMeasure && onUpdate) {
      onUpdate({ riskReductionMeasureId: riskReductionMeasure.id, ...formData })
      return
    }

    onCreate(formData)
    setFormData(defaultRiskReductionMeasureFormData)
  }, [canSubmit, formData, isEdit, onCreate, onUpdate, riskReductionMeasure])

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className='sm:max-w-3xl' showCloseButton={!isPending}>
        <DialogHeader>
          <DialogTitle>
            {isEdit
              ? t('riskReductionMeasuresLibraryPage.formDialog.editTitle')
              : t('riskReductionMeasuresLibraryPage.formDialog.addTitle')}
          </DialogTitle>
        </DialogHeader>

        <RiskReductionMeasureForm
          data={formData}
          onChange={setFormData}
          safetyFunctions={safetyFunctions}
        />

        <DialogFooter>
          <Button variant='outline' onClick={handleClose} disabled={isPending}>
            {t('common:actions.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isEdit
              ? t('riskReductionMeasuresLibraryPage.formDialog.updateButton')
              : t('common:actions.add')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
